/*
  Slideshow carousel (sections/slideshow-carousel.liquid)
  Native scroll-snap carousel: swipe / trackpad scroll moves slides,
  prev/next buttons, dots and arrow keys scroll one slide at a time.
  No autoplay.
*/
(function () {
  if (window.customElements.get('slideshow-carousel')) return;

  class SlideshowCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.slideshow-carousel__track');
      if (!this.track) return;

      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('.slideshow-carousel__slide'));
      this.controls = this.querySelector('.slideshow-carousel__controls');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.dotsWrap = this.querySelector('.slideshow-carousel__dots');
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-carousel-dot]'));

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.update = this.update.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.move(-1));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.move(1));
      }
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.carouselDot, 10)));
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.update);
      }

      // Theme editor: bring the selected slide into view
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.update);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    // Distance between the starts of two neighbouring slides (slide width + gap)
    get step() {
      if (this.slides.length < 2) return this.track.clientWidth || 1;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft || 1;
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    get currentIndex() {
      if (this.track.scrollLeft >= this.maxScroll - 2 && this.maxScroll > 2) {
        return this.slides.length - 1;
      }
      return Math.round(this.track.scrollLeft / this.step);
    }

    goTo(index) {
      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      const left = this.slides[target].offsetLeft - this.slides[0].offsetLeft;
      this.track.scrollTo({ left: Math.min(left, this.maxScroll), behavior: this.scrollBehavior() });
    }

    move(direction) {
      const index = Math.round(this.track.scrollLeft / this.step);
      this.goTo(index + direction);
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    onScroll() {
      this.update();
    }

    onKeydown(event) {
      if (event.target !== this.track) return;
      const keys = { ArrowLeft: -1, ArrowRight: 1 };
      if (keys[event.key]) {
        event.preventDefault();
        this.move(keys[event.key]);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1);
      }
    }

    onBlockSelect(event) {
      const index = this.slides.indexOf(event.target);
      if (index > -1) this.goTo(index);
    }

    update() {
      const scrollable = this.maxScroll > 2;
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= this.maxScroll - 2;

      if (this.controls) this.controls.hidden = !scrollable;
      if (this.dotsWrap) this.dotsWrap.hidden = !scrollable;

      this.setDisabled(this.prevButton, atStart, this.nextButton);
      this.setDisabled(this.nextButton, atEnd, this.prevButton);

      const active = this.currentIndex;
      this.dots.forEach((dot, i) => {
        if (i === active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    // Keep keyboard focus on a usable control when a button becomes disabled
    setDisabled(button, disabled, fallback) {
      if (!button || button.disabled === disabled) return;
      const hadFocus = document.activeElement === button;
      button.disabled = disabled;
      if (disabled && hadFocus && fallback && !fallback.disabled) fallback.focus();
    }
  }

  window.customElements.define('slideshow-carousel', SlideshowCarousel);
})();
